import { useEffect, useState } from 'react'
import { PAGE_TITLES, useDocumentTitle } from '../lib/documentTitle'
import { useAuthStore } from '../stores/authStore'
import { getInterviewStatus, getSocToday, type SocToday } from '../lib/api'
import {
  CLIENT_PLACE_INTENT_KEY,
  CLIENT_PLACED_DECOY_KEY,
  CLIENT_PLACE_ERROR_KEY,
  CLIENT_PLACE_HANDOFF_KEY,
} from '../lib/sessionKeys'
import { ClientDashboard } from './ClientDashboard'
import { SocShell } from '../components/soc/SocShell'
import { OwnerHome } from '../components/soc/OwnerHome'
import { MemberHome } from '../components/soc/MemberHome'
import { StaffNoticeLink } from '../components/StaffNoticeLink'

// /client: the customer page (BRIEF-E2b; docs/design/customer-page/). The
// owner's home is fed by the E2a routes under /api/v1/soc/. When /soc/today
// says scope "self" the page is a plain member's (Member.dc.html): who is a
// member is the route's to say, never this page's.
//
// Two moments still belong to the earlier home (ClientDashboard), which keeps
// its own tested behaviour for them and is otherwise no longer mounted here:
// - a sign-in-to-place handoff from LoginPage (the decoy, or its error, is
//   shown by HomeTripwiresCard, which only ClientDashboard mounts), and
// - a first meeting that status says is not complete (it sits in place).
// A status that throws is not an owed meeting: the customer page paints.
//
// BRIEF-B11: until the owner has told their staff, the owner's home (and the
// earlier home a fresh owner sees) carries StaffNoticeLink. A member's home
// never mounts it.

type Gate = 'pending' | 'earlier' | 'page'

function hasPlaceHandoff(): boolean {
  return (
    !!sessionStorage.getItem(CLIENT_PLACE_INTENT_KEY) ||
    !!sessionStorage.getItem(CLIENT_PLACED_DECOY_KEY) ||
    !!sessionStorage.getItem(CLIENT_PLACE_ERROR_KEY) ||
    !!sessionStorage.getItem(CLIENT_PLACE_HANDOFF_KEY)
  )
}

export function CustomerHomePage() {
  useDocumentTitle(PAGE_TITLES.clientHome)
  const principalId = useAuthStore((s) => s.principal?.id)
  const [hadPlaceHandoff] = useState(() => hasPlaceHandoff())
  const [gate, setGate] = useState<Gate>(hadPlaceHandoff ? 'earlier' : 'pending')

  useEffect(() => {
    if (hadPlaceHandoff) return
    if (!principalId) {
      setGate('pending')
      return
    }
    let cancelled = false
    getInterviewStatus().then(
      (st) => !cancelled && setGate(st.status === 'complete' ? 'page' : 'earlier'),
      () => !cancelled && setGate('page'),
    )
    return () => {
      cancelled = true
    }
  }, [principalId, hadPlaceHandoff])

  if (gate === 'earlier') {
    return (
      <>
        <StaffNoticeLink className="bg-cream px-4 pt-4 sm:px-6" />
        <ClientDashboard />
      </>
    )
  }
  if (gate === 'pending') return <div className="min-h-screen bg-cream" />
  return <SocHome />
}

function SocHome() {
  const [today, setToday] = useState<SocToday | null>(null)
  const [error, setError] = useState(false)

  useEffect(() => {
    let cancelled = false
    getSocToday().then(
      (data) => !cancelled && setToday(data),
      () => !cancelled && setError(true),
    )
    return () => {
      cancelled = true
    }
  }, [])

  if (error) {
    return (
      <SocShell current="today">
        <p className="m-0 text-lg text-[#2b3540]" role="alert">
          We could not load today's read from your SOC just now. Nothing on this page is a guess,
          so it shows nothing until it can. Please try again shortly.
        </p>
      </SocShell>
    )
  }
  if (today === null) {
    return (
      <SocShell current="today">
        <p className="m-0 text-lg text-[#6b5a3e]" role="status">
          Loading today's read…
        </p>
      </SocShell>
    )
  }
  if (today.scope === 'self') return <MemberHome today={today} />
  return (
    <SocShell current="today">
      <StaffNoticeLink />
      <OwnerHome today={today} />
    </SocShell>
  )
}
